import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getTransaction } from "../../../services/transactionApi";
import { FaCheckCircle } from "react-icons/fa";
import { useMemo, useState } from "react";
import moment from "moment";
import "moment/locale/id";
import { IoMdCopy } from "react-icons/io";
import { useNotification } from "../../../components/context/NotificationContext";

moment.locale("id");

const formatRupiah = (number) => {
  return number ? Number(number).toLocaleString("id-ID") : 0;
};

const STATUS_CONFIG = {
  0: {
    label: "Unknown Status",
    bgColor: "bg-slate-100",
    textColor: "text-slate-700",
  },
  1: {
    label: "Menunggu Pembayaran",
    bgColor: "bg-orange-100",
    textColor: "text-orange-700",
  },
  2: {
    label: "Diproses",
    bgColor: "bg-purple-100",
    textColor: "text-purple-700",
  },
  3: {
    label: "Dikirim",
    bgColor: "bg-blue-100",
    textColor: "text-blue-700",
  },
  4: { label: "Selesai", bgColor: "bg-green-100", textColor: "text-green-700" },
  5: { label: "Dibatalkan", bgColor: "bg-red-100", textColor: "text-red-700" },
};

const getStatusConfig = (statusValue) =>
  STATUS_CONFIG[statusValue] ?? STATUS_CONFIG[1];

const formatOrderDateTimeId = (raw) => {
  if (raw == null || String(raw).trim() === "") return "—";
  const s = String(raw).trim();
  const attempts = [
    () => moment(s, "DD/MM/YYYY HH:mm", true),
    () => moment(s, "DD/MM/YYYY HH:mm:ss", true),
    () => moment(s, "DD/MM/YYYY H:mm", true),
    () => moment(s.replace(/,/g, ""), "DD MMM YYYY HH:mm", true),
    () => moment(s, moment.ISO_8601, true),
    () => moment(s),
  ];
  for (const tryParse of attempts) {
    const m = tryParse();
    if (m.isValid()) {
      const month = m.format("MMMM");
      const monthCap = month.charAt(0).toUpperCase() + month.slice(1);
      return `${m.format("D")} ${monthCap} ${m.format("YYYY, [Jam] HH:mm")}`;
    }
  }
  return s;
};

const parseStatusTimestamp = (createdAt) => {
  if (!createdAt) return 0;
  const t = Date.parse(String(createdAt).replace(/,/g, ""));
  return Number.isNaN(t) ? 0 : t;
};

const sortStatusListChronological = (list) => {
  if (!list?.length) return [];
  return [...list].sort((a, b) => {
    const diff =
      parseStatusTimestamp(a.created_at) - parseStatusTimestamp(b.created_at);
    if (diff !== 0) return diff;
    return (a.id ?? 0) - (b.id ?? 0);
  });
};

const cardShell =
  "rounded-xl border border-slate-200/90 bg-white p-3 md:p-4 md:shadow-md md:ring-1 md:ring-slate-200/60";

const SkeletonLoader = () => {
  return (
    <div className="pb-24 md:pb-8">
      <div className="h-9 w-28 bg-slate-200 rounded-lg animate-pulse mb-6" />
      <div className="grid grid-cols-1 md:grid-cols-12 md:gap-6 xl:gap-8 gap-6 animate-pulse">
        <div className="md:col-span-5 xl:col-span-4 space-y-6">
          <div className={`${cardShell} space-y-3`}>
            <div className="h-5 bg-slate-200 rounded w-full" />
            <div className="h-4 bg-slate-200 rounded w-4/5" />
            <div className="h-8 bg-slate-200 rounded w-2/5" />
          </div>
          <div className={`${cardShell} space-y-4`}>
            {[1, 2, 3].map((item, index) => (
              <div key={item}>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-slate-200 rounded-full shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-200 rounded w-1/2" />
                  </div>
                </div>
                {index < 2 && <hr className="my-2 w-[80%] ml-auto" />}
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-7 xl:col-span-8 space-y-6">
          <div className={`${cardShell} space-y-3`}>
            <div className="h-5 bg-slate-200 rounded w-40" />
            <div className="h-4 bg-slate-200 rounded w-full" />
            <div className="h-4 bg-slate-200 rounded w-5/6" />
          </div>
          <div className={`${cardShell} space-y-3`}>
            <div className="h-5 bg-slate-200 rounded w-28 mb-2" />
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="flex items-center justify-between gap-2"
              >
                <div className="flex gap-2 flex-1">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-slate-200 rounded-md shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-full" />
                    <div className="h-3 bg-slate-200 rounded w-24" />
                  </div>
                </div>
                <div className="h-4 bg-slate-200 rounded w-20" />
              </div>
            ))}
            <hr />
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="flex items-center justify-between">
                <div className="h-4 bg-slate-200 rounded w-32" />
                <div className="h-4 bg-slate-200 rounded w-24" />
              </div>
            ))}
            <hr />
            <div className="flex items-center justify-between">
              <div className="h-5 bg-slate-200 rounded w-16" />
              <div className="h-5 bg-slate-200 rounded w-32" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailTransaction = () => {
  const { orderNumber } = useParams();
  const { showNotification } = useNotification();
  const [copiedResi, setCopiedResi] = useState("");

  const {
    data: transactionDetailData,
    isFetching: isTransactionDetailLoading,
  } = useQuery({
    queryKey: ["transaction", "detail", orderNumber],
    queryFn: () => getTransaction(orderNumber),
    enabled: Boolean(orderNumber),
  });

  const sortedStatusList = useMemo(
    () => sortStatusListChronological(transactionDetailData?.status_list),
    [transactionDetailData?.status_list],
  );

  const headerStatusConfig = useMemo(() => {
    const trx = transactionDetailData?.status_trx;
    return getStatusConfig(typeof trx === "number" ? trx : Number(trx) || 0);
  }, [transactionDetailData?.status_trx]);

  const formattedOrderDate = useMemo(
    () => formatOrderDateTimeId(transactionDetailData?.created_at),
    [transactionDetailData?.created_at],
  );

  const shipping = transactionDetailData?.shipping;
  const payment = transactionDetailData?.payment;

  const handleCopyResi = async () => {
    const resi = shipping?.shipping_number_tracking;
    if (!resi || String(resi).trim() === "") return;

    try {
      await navigator.clipboard.writeText(String(resi));
      showNotification("Nomor resi berhasil disalin!", "success");
      setTimeout(() => setCopiedResi(""), 2000);
    } catch (error) {
      showNotification("Nomor resi gagal disalin!", "error");
      setTimeout(() => setCopiedResi(""), 2000);
    }
  };

  const getIconForItem = (index, totalItems) => {
    const isLatest = index === totalItems - 1;
    return (
      <FaCheckCircle
        className={isLatest ? "text-secondary" : "text-slate-300"}
        size={20}
      />
    );
  };

  return (
    <div className="w-full pt-2 md:pt-4 md:pb-10 px-3 md:px-0">
      {isTransactionDetailLoading ? (
        <SkeletonLoader />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-12 md:gap-6 xl:gap-8 gap-6">
            <div className="md:col-span-5 xl:col-span-4 space-y-6">
              <div className={`${cardShell}`}>
                <div className="grid grid-cols-[auto_auto_1fr] items-baseline gap-x-2 gap-y-1 text-md">
                  <span className="text-slate-600">No. Pesanan</span>
                  <span className="text-slate-500 tabular-nums">:</span>
                  <span className="font-medium text-slate-800 tabular-nums min-w-0 break-all">
                    {transactionDetailData?.order_number ?? "—"}
                  </span>

                  <span className="text-slate-600">Tanggal</span>
                  <span className="text-slate-500 tabular-nums">:</span>
                  <span className="font-medium text-slate-800 tabular-nums min-w-0">
                    {formattedOrderDate}
                  </span>

                  <span className="text-slate-600">Status</span>
                  <span className="text-slate-500 tabular-nums">:</span>
                  <div className="min-w-0 justify-self-start">
                    <div
                      className={`inline-flex rounded-full px-2.5 py-1 text-sm font-medium ${headerStatusConfig.bgColor}`}
                    >
                      <span
                        className={`${headerStatusConfig.textColor} font-medium`}
                      >
                        {headerStatusConfig.label}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-semibold text-lg md:text-xl mb-2 text-slate-800">
                  Status Pesanan
                </h2>
                {sortedStatusList.length > 0 ? (
                  <div
                    className={`${cardShell} bg-slate-50/80 md:bg-white border-slate-200/80`}
                  >
                    {sortedStatusList.map((item, index) => (
                      <div key={item.id} className="flex gap-3 items-stretch">
                        <div className="relative flex w-5 shrink-0 flex-col items-center self-stretch">
                          <div className="relative z-10 rounded-full bg-transparent pt-0.5 md:bg-white md:shadow-sm md:ring-1 md:ring-slate-100">
                            {getIconForItem(index, sortedStatusList.length)}
                          </div>
                          {index < sortedStatusList.length - 1 ? (
                            <div
                              className="absolute left-1/2 top-[24px] bottom-0 w-0.5 -translate-x-1/2 bg-slate-200"
                              aria-hidden
                            />
                          ) : null}
                        </div>
                        <div className="min-w-0 flex-1 pb-3 last:pb-0 pt-0.5">
                          <p className="text-md font-medium text-slate-800">
                            {item.message}
                          </p>
                          <p className="text-sm text-slate-500 mt-0.5">
                            {item.created_at}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className={`${cardShell} text-center text-slate-500 text-sm`}
                  >
                    Belum ada riwayat status untuk pesanan ini.
                  </div>
                )}
              </div>
            </div>

            <div className="md:col-span-7 xl:col-span-8 space-y-6">
              <div>
                <h2 className="font-semibold text-lg md:text-xl mb-2 text-slate-800">
                  Info Pengiriman
                </h2>
                <div className={cardShell}>
                  <div className="">
                    <h3 className="font-semibold text-slate-800 mb-1 text-base md:text-[17px]">
                      {shipping?.shipping_user ?? "—"}
                    </h3>
                    <p className="text-md text-primary font-medium mb-1">
                      {shipping?.shipping_phone ?? "—"}
                    </p>
                    <p className="text-sm md:text-md text-slate-600 break-words leading-relaxed">
                      {shipping?.shipping_address ?? "—"}
                    </p>
                  </div>
                  <hr className="my-2" />
                  <div className="text-md text-slate-600 font-medium">
                    <p>Kurir: {shipping?.shipping_method_name ?? "—"}</p>
                    <div className="flex flex-wrap items-center gap-1">
                      <p>
                        No. Resi:{" "}
                        <span className="text-primary">
                          {shipping?.shipping_number_tracking ?? "—"}
                        </span>
                      </p>
                      {shipping?.shipping_number_tracking ? (
                        <button
                          onClick={handleCopyResi}
                          className="flex items-center gap-2 p-1 lg:px-3 lg:py-1 lg:border-2 text-primary rounded-full lg:rounded-lg hover:bg-indigo-100 transition-colors text-md cursor-pointer"
                        >
                          <IoMdCopy size={16} />
                          <p className="hidden lg:block">
                            {copiedResi ? "Tersalin!" : "Salin"}
                          </p>
                        </button>
                      ) : null}
                    </div>
                    {copiedResi ? (
                      <p className="mt-1 text-sm text-green-600">
                        {copiedResi}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-semibold text-lg md:text-xl mb-2 text-slate-800">
                  Ringkasan Pesanan
                </h2>
                <div className={cardShell}>
                  <h3 className="font-semibold text-slate-800 mb-2 text-base">
                    Daftar Obat
                  </h3>
                  <div className="w-full flex flex-col gap-1">
                    {transactionDetailData?.cart?.map((item, index) => (
                      <div
                        key={index}
                        className="w-full flex items-end justify-between gap-3"
                      >
                        <div className="w-full flex gap-3 min-w-0">
                          <div className="border border-slate-200/80 rounded-lg w-12 h-12 md:w-14 md:h-14 overflow-hidden shrink-0 bg-slate-50">
                            <img
                              src={item.image?.[0]}
                              alt={item.product_name}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          <div className="min-w-0">
                            <p className="text-slate-800 text-md font-medium line-clamp-2 leading-tight">
                              {item.product_name}
                            </p>
                            <p className="text-slate-500 text-sm tabular-nums mt-0.5">
                              {item.quantity}x Rp{formatRupiah(item.price)}
                            </p>
                          </div>
                        </div>

                        <p className="font-semibold text-slate-800 text-md tabular-nums shrink-0">
                          Rp{formatRupiah(item.total_price)}
                        </p>
                      </div>
                    ))}
                  </div>
                  <hr className="my-4 border-slate-200/80" />
                  <div className="flex flex-col gap-1 text-md">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-slate-700 shrink-0">
                        Metode Pembayaran
                      </p>
                      <p className="font-medium text-slate-800 truncate text-right min-w-0">
                        {payment?.payment_method_name ?? "—"}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-slate-700">
                        Subtotal Produk
                      </p>
                      <p className="font-medium text-slate-800 tabular-nums">
                        Rp{formatRupiah(payment?.subtotal)}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-slate-700">
                        Biaya Pengiriman
                      </p>
                      <p className="font-medium text-slate-800 tabular-nums">
                        Rp{formatRupiah(shipping?.shipping_price)}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-slate-700">
                        Biaya Layanan
                      </p>
                      <p className="font-medium text-slate-800 tabular-nums">
                        Rp{formatRupiah(payment?.fee)}
                      </p>
                    </div>
                  </div>
                  <hr className="my-4 border-slate-200/80" />
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-slate-800 text-base md:text-lg">
                      Total
                    </p>
                    <p className="font-semibold text-error tabular-nums text-base md:text-lg">
                      Rp{formatRupiah(payment?.total_transaction)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="fixed md:static bottom-0 left-0 right-0 z-[99] mt-6 md:mt-10 bg-white/95 md:bg-white/90 md:backdrop-blur-sm md:rounded-2xl py-3.5 md:py-4 px-4 md:px-6 flex flex-wrap items-center justify-center
              gap-1 md:gap-2 text-md md:text-base font-medium border-t border-slate-200/80 shadow-[0_-4px_24px_rgba(0,0,0,0.06)]
              md:border md:border-white/80 md:shadow-md"
          >
            <p className="text-slate-700">Butuh bantuan?</p>
            <p className="text-primary font-semibold">Chat via WhatsApp</p>
          </div>
        </>
      )}
    </div>
  );
};

export default DetailTransaction;
